import type {
  CurrentReport,
  DeviceStatus,
  GeneralSettings,
  OpenWork,
  PaymentMethod,
  PeriodStats,
  QuarterTable,
  VatRate,
  ZCorrection,
  ZPlatform,
  ZReport,
  ZSummary,
} from "../../api/types";
import { t } from "../../i18n";
import { shopAddress } from "../general/general";
import { formatAmount, parseAmount, type Cents } from "../../lib/money";
import { formatTime } from "../../lib/time";

/** VAT letters as in the prototype: A 21%, B 12%, C 6%. */
export const VAT_LETTERS: Readonly<Record<number, string>> = { 21: "A", 12: "B", 6: "C" };

/** The payment methods in the order of the prototype. */
export const PAYMENT_ORDER: readonly PaymentMethod[] = [
  "cash",
  "bancontact",
  "card",
  "bank_qr",
  "meal_voucher",
  "online",
];

/** The X (running total) or a Z, as printed on the 80 mm receipt printer. */
export interface ReportPrint {
  kind: "x" | "z";
  /** `company`: the firm behind the shop, above its address; not in a Z from before 13/10/2026. */
  shop: { name: string; company?: string | null; address: string | null; vatNumber: string | null };
  /** The Z's number (only for a Z). */
  number: number | null;
  /** When the X was printed or the Z closed. */
  at: string;
  /** The business day it is about (06:00 to 06:00), "2026-10-10"; null for an X queued by an older server. */
  businessDay: string | null;
  /** The first and the last order of that day; null without orders. */
  firstOrderAt: string | null;
  lastOrderAt: string | null;
  /** Z: closed after its day had ended ("LAAT AFGESLOTEN"). */
  late: boolean;
  /** X: the last Z, where it runs from. */
  since: { number: number; closedAt: string } | null;
  stats: PeriodStats;
  /** The platforms typed in on the Z page: added to the day total. */
  platforms: ZPlatform[];
  /** The linked platforms ("Waarvan via platformen"): already in the total, never added again. */
  linkedPlatforms: ZPlatform[];
  floatCents: Cents;
  expectedCashCents: Cents;
  /** X: what is still open ("NOG OPEN"). */
  open: string[];
  /** Z: orders removed after the closing ("HERREKEND"); `original`: the Z as printed before them. */
  corrections: ZCorrection[];
  original: boolean;
}

/** The X as printed now, with the shop as on every receipt (Algemeen; the onboarding details until loaded). */
export function xPrint(
  current: CurrentReport,
  shop: DeviceStatus["shop"],
  general: GeneralSettings | null,
  at: Date,
): ReportPrint {
  return {
    kind: "x",
    shop: general
      ? {
          name: general.receiptName,
          company: general.company || null,
          address: shopAddress(general),
          vatNumber: general.vatNumber || null,
        }
      : {
          name: shop.name.toUpperCase(),
          company: shop.firm?.name ?? null,
          address: shop.firm?.address ?? null,
          vatNumber: shop.firm?.vatNumber ?? null,
        },
    number: null,
    at: at.toISOString(),
    businessDay: current.businessDay,
    firstOrderAt: current.firstOrderAt ?? null,
    lastOrderAt: current.lastOrderAt ?? null,
    late: false,
    since: current.since,
    stats: current.stats,
    platforms: [],
    linkedPlatforms: current.platformsAuto ?? [],
    floatCents: current.floatCents,
    expectedCashCents: current.expectedCashCents,
    open: openLines(current.open),
    corrections: [],
    original: false,
  };
}

/** A Z exactly as it was closed (also later, from the archive). */
export function zPrint(report: ZReport): ReportPrint {
  return {
    kind: "z",
    shop: report.shop,
    number: report.number,
    at: report.closedAt,
    businessDay: report.businessDay,
    firstOrderAt: report.firstOrderAt ?? null,
    lastOrderAt: report.lastOrderAt ?? null,
    late: report.late ?? false,
    since: null,
    stats: report.stats,
    platforms: report.platforms,
    linkedPlatforms: report.linkedPlatforms ?? [],
    floatCents: report.floatCents,
    expectedCashCents: report.expectedCashCents,
    open: [],
    corrections: report.corrections,
    original: report.original,
  };
}

/**
 * One line of the Z archive (Antalya, 17/10/2026: "Platformen € 0,00" since the koppeling): Kassa is what the till
 * sold itself, the Z's revenue without the orders of the linked platforms; Platformen are those typed in on the Z
 * page and the linked ones; the day total stays the Z's revenue plus the platforms typed in (those are not in Tafo).
 */
export function archiveFigures(row: Pick<ZSummary, "revenueCents" | "platformsCents" | "linkedPlatformsCents">): {
  tillCents: Cents;
  platformsCents: Cents;
  dayTotalCents: Cents;
} {
  const linked = row.linkedPlatformsCents ?? 0;
  return {
    tillCents: row.revenueCents - linked,
    platformsCents: row.platformsCents + linked,
    dayTotalCents: row.revenueCents + row.platformsCents,
  };
}

/** "Eerst afwerken:" one line per kind of open work; empty when the Z can be closed. */
export function openLines(open: OpenWork): string[] {
  const lines: string[] = [];
  if (open.deliveries > 0) lines.push(t.reports.open.deliveries(open.deliveries));
  if (open.couriers.length > 0) lines.push(t.reports.open.couriers(open.couriers.join(", ")));
  if (open.tables.length > 0) lines.push(t.reports.open.tables(open.tables.join(", ")));
  if (open.other > 0) lines.push(t.reports.open.other(open.other));
  if (open.parked > 0) lines.push(t.reports.open.parked(open.parked));
  return lines;
}

/**
 * One platform on the Z page, as typed: its day total and what was paid online (Furkan, 13/10/2026: "je
 * moet ook online betaalde van platformen vragen"; only the Z-bon uses it).
 */
export interface PlatformDraft {
  name: string;
  revenue: string;
  online: string;
}

export interface PlatformFigures {
  name: string;
  revenueCents: Cents;
  onlineCents: Cents;
}

/** An amount as it is typed in a field of the Z page: 120000 → "1200,00" (no dots, so it reads back). */
export function typedAmount(cents: Cents): string {
  return (cents / 100).toFixed(2).replace(".", ",");
}

/** What was typed on the Z page for one day, as this till kept it. */
export interface KeptPlatforms {
  /** A draft of 12/10/2026 has no "online" yet. */
  platforms: (Omit<PlatformDraft, "online"> & { online?: string })[];
  /** When the day's Z was closed at the moment this was typed; null or absent: the day had no Z then. */
  basedOn?: string | null;
}

/**
 * The fields of the Z page, for the platforms the shop has now: what was typed on this till, otherwise what
 * the day's Z was closed with (Furkan, 13/10/2026: "waarden niet op 0 zetten wat ik intik tot ik verander").
 * What was typed before the day got another Z (closed on another till) is out of date: that Z wins, so a
 * new Z never goes back to older figures.
 */
export function platformFields(
  names: readonly string[],
  kept: KeptPlatforms | null,
  closed: { closedAt: string; platforms?: ZPlatform[] } | null,
): PlatformDraft[] {
  const typed = kept !== null && (kept.basedOn ?? null) === (closed?.closedAt ?? null) ? kept.platforms : [];
  const inZ = closed?.platforms ?? [];
  return names.map((name) => {
    const typedBefore = typed.find((platform) => platform.name === name);
    if (typedBefore) return { name, revenue: typedBefore.revenue, online: typedBefore.online ?? "" };
    const closedWith = inZ.find((platform) => platform.name === name);
    return {
      name,
      revenue: closedWith ? typedAmount(closedWith.revenueCents) : "",
      online: closedWith?.onlineCents ? typedAmount(closedWith.onlineCents) : "",
    };
  });
}

/**
 * Reads the platforms of the Z page: an empty field is 0. Lists the platforms where more was paid online
 * than the day total and those with an amount that cannot be read.
 */
export function readPlatforms(drafts: readonly PlatformDraft[]): {
  figures: PlatformFigures[];
  tooHigh: string[];
  unreadable: string[];
} {
  const unreadable: string[] = [];
  const figures = drafts.map((draft) => {
    const read = (text: string) => (text.trim() === "" ? 0 : parseAmount(text));
    const revenue = read(draft.revenue);
    const online = read(draft.online);
    if (revenue === null || online === null) unreadable.push(draft.name);
    return { name: draft.name, revenueCents: revenue ?? 0, onlineCents: online ?? 0 };
  });
  const tooHigh = figures.filter((figure) => figure.onlineCents > figure.revenueCents).map((figure) => figure.name);
  return { figures, tooHigh, unreadable };
}

/** The rates of the X and Z bon, each with its own line, also at € 0,00 (Furkan, 13/10/2026). */
const BON_VAT_RATES: readonly VatRate[] = [6, 12, 21];

/**
 * The VAT per rate with the online platforms: their revenue always counts in the 6% (Furkan, 13/10/2026:
 * "platformen altijd in de 6% btw"), incl. VAT, so its base is that revenue / 1,06. The three rates always,
 * also at € 0,00.
 */
export function vatWithPlatforms(stats: PeriodStats, platformsCents: Cents): PeriodStats["vat"] {
  const platformsBase = Math.round(platformsCents / 1.06);
  return BON_VAT_RATES.map((rate) => {
    const till = stats.vat.find((vat) => vat.rate === rate) ?? { rate, baseCents: 0, vatCents: 0, grossCents: 0 };
    return rate === 6
      ? {
          rate,
          baseCents: till.baseCents + platformsBase,
          vatCents: till.vatCents + platformsCents - platformsBase,
          grossCents: till.grossCents + platformsCents,
        }
      : till;
  });
}

/** The revenue incl. VAT per rate on the X and Z bon: the till's own revenue of that rate, the platforms in the 6%. */
export function bonVatLines(
  stats: PeriodStats,
  platforms: readonly ZPlatform[],
): { rate: VatRate; grossCents: Cents }[] {
  const platformsCents = platforms.reduce((total, platform) => total + platform.revenueCents, 0);
  return vatWithPlatforms(stats, platformsCents).map(({ rate, grossCents }) => ({ rate, grossCents }));
}

/**
 * The payment methods on the X and Z bon: what the till received, and of each platform what was paid online
 * under Bancontact and the rest of its day total under Contant (Furkan, 13/10/2026: "gewoon onder
 * bancontact online betaald en de rest bij cash"). A platform of a Z that was not asked what was paid online
 * (12/10/2026) counts nowhere, as that Z was printed. Methods without an amount are left out.
 */
export function bonPaymentLines(
  stats: PeriodStats,
  platforms: readonly ZPlatform[],
): { method: PaymentMethod; cents: Cents }[] {
  const asked = platforms.filter((platform) => platform.onlineCents !== undefined);
  const online = asked.reduce((total, platform) => total + (platform.onlineCents ?? 0), 0);
  const atTheDoor = asked.reduce((total, platform) => total + platform.revenueCents - (platform.onlineCents ?? 0), 0);
  const extra: Partial<Record<PaymentMethod, Cents>> = { bancontact: online, cash: atTheDoor };
  return PAYMENT_ORDER.map((method) => ({ method, cents: stats.payments[method] + (extra[method] ?? 0) })).filter(
    (line) => line.cents !== 0,
  );
}

/** "zaterdag 10/10/2026": the day a Z is about, written out (Furkan, 11/10/2026). */
export function dayName(day: string): string {
  return new Date(`${day}T12:00:00`).toLocaleDateString("nl-BE", {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** "za 10/10": short, for the orange bar and the buttons of the forgotten days. */
export function shortDayName(day: string): string {
  return new Date(`${day}T12:00:00`).toLocaleDateString("nl-BE", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  });
}

/** "za 26/09/2026 23:12", as in the Z archive of the prototype. */
export function zDateTime(iso: string): string {
  const moment = new Date(iso);
  const day = moment.toLocaleDateString("nl-BE", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  return `${day} ${formatTime(moment)}`;
}

/** "2026-09-06" → "6/9/2026", as in the quarterly overview of the prototype. */
export function quarterDate(day: string): string {
  const [year, month, date] = day.split("-").map(Number);
  return new Date(year ?? 0, (month ?? 1) - 1, date ?? 1).toLocaleDateString("nl-BE");
}

/** A cell of the quarterly overview as text: amounts without "€", dates as 6/9/2026. */
export function quarterCell(kind: QuarterTable["columns"][number]["kind"], value: string | number | null): string {
  if (value === null) return "";
  if (kind === "date" && typeof value === "string") return quarterDate(value);
  if (kind === "money" && typeof value === "number") return formatAmount(value);
  return String(value);
}

/** The totals line under the overview: "Totaal", "n dagen", then the sums (prototype). */
export function quarterTotals(table: QuarterTable): string[] {
  return table.columns.map((column, index) => {
    if (column.kind === "date") return t.reports.accountant.total;
    if (column.header === "Z-nr") return t.reports.accountant.days(table.rows.length);
    const sum = table.rows.reduce((total, row) => total + (typeof row[index] === "number" ? row[index] : 0), 0);
    return quarterCell(column.kind, sum);
  });
}
